import { NgsFragmentAnalysisService } from './../ngs-fragment-analysis/ngs-fragment-analysis.service';
import { AssayType } from './../masterdata/models/assayType-enum';
import { SampleDetailsDTO } from './../ngs-work-list/model/sample-details-dto';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators, FormBuilder } from '@angular/forms';
import { FilePickerDirective, ReadFile, ReadMode } from 'ngx-file-helpers';
import { MachineGeneratedFile } from '../ngs-volume-adjustment/machine-generated-file';
import { NGSLibraryQCService } from './ngs-library-qc-service';
import { MessagesService } from '../messages/messages.service';
import { ErrorHandler } from '../utils/error-handler';
import { FAPlateDTO } from '../ngs-fragment-analysis/model/fa-plate-dto';
import { LibQCFAWorkFileRequest } from './model/lib-qc-fa-workfile-request';
import { DatePipe } from '@angular/common';
import { NGSWorkfileResponse } from '../ngs-volume-adjustment/ngs-workfile-response';
import { saveAs as saveByFileSaver } from "file-saver";

@Component({
  selector: 'app-ngs-library-qc',
  templateUrl: './ngs-library-qc.component.html',
  styleUrls: ['./ngs-library-qc.component.css']
})
export class NGSLibraryQCComponent implements OnInit {

  errorHandler: ErrorHandler = new ErrorHandler;

  // Plate view variables
  plateData : SampleDetailsDTO[] = [];
  positionAndSampleMap = new Map();
  originalPositionAndSampleMap = new Map();
  assayTypeCounts = {
    SSNGS : 0,
    HSNGS : 0
  }
  plateViewShow = false;
  assayTypeChangeMap = new Map();
  generareWorkFileForm: FormGroup;

  assayTypeChangeClass = 'normal';

  selectedMachineType: string;
  selectedAssayType: string;
  faPlateBarcode: string;

  faPlates: FAPlateDTO[];

  readMode = ReadMode.Text;
  libFLXFileUploadForm = new FormGroup({
    createdBy: new FormControl('', Validators.required),
    machineBarcode: new FormControl('', Validators.required),
    flxFileName: new FormControl('', Validators.required)
  });

  columnHeaders = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  rowHeaders = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

  flxFileContent: string;
  status: string;

  showPlateData = false;
  blocked = false;

  sampleBarcodeList: string[];

  @ViewChild('filePicker',{static : false})
  private flxFilePicker: FilePickerDirective;

  constructor(private datePipe: DatePipe, private formBuilder: FormBuilder, private ngsLibraryQCService: NGSLibraryQCService, private _messagesService: MessagesService, private ngsFragmentAnalysisService: NgsFragmentAnalysisService) { }

  ngOnInit() {
    this.plateData = [];
    this.sampleBarcodeList = [];
    this.assayTypeCounts = {
      SSNGS : 0,
      HSNGS : 0
    };
    this.assayTypeChangeMap = new Map();
    this.generareWorkFileForm = this.formBuilder.group({
      'faPlateBarcode': new FormControl('', Validators.required),
      'machineType': new FormControl('', Validators.required)
    });
    this.assayTypeChangeClass = 'normal';
  }

  onFlxFilePicked(file: ReadFile) {
    this.libFLXFileUploadForm.patchValue({'flxFileName': file.name});
    this.flxFileContent = file.content;
    console.log(this.libFLXFileUploadForm.value);
  }

  onFlxFileReadEnd(fileCount: number) {
    this.status = `Read ${fileCount} file(s) on ${new Date().toLocaleTimeString()}.`;
    this.flxFilePicker.reset();
  }

  focusMachinebarcode() {
    document.getElementById('machinebarcode').focus();
  }

  /**
   * Upload FLX File and get back plate view data
   */
  uploadFlxFile() {
    this.blocked = true;
    this.ngOnInit();
    let hamiltonFile: MachineGeneratedFile = new MachineGeneratedFile(this.libFLXFileUploadForm.get('flxFileName').value, this.flxFileContent);
    this.ngsLibraryQCService.getPlateViewFromFLXFile(hamiltonFile).subscribe(
      results => {
        if(!results) {
          this.blocked = false;
          return;
        }
        this.plateViewShow = true;
        this.plateData = results.data;
        this.createMapOfPositionAndSampleData(this.plateData);
        this._messagesService.addSuccessMessage({
          severity: 'success', summary: 'Upload finished',
          detail: 'FLX file has been uploaded successfully'
        });
        this.blocked = false;
    },
    error => {
      this.blocked = false;
      let message = this.errorHandler.handleError(error);
      this._messagesService.addErrorMessage(message);
    }
  );
  }

  /**
   * Create required maps for plate view out of recieved data from front end.
   * 
   * @param data 
   */
  createMapOfPositionAndSampleData(data: SampleDetailsDTO[]) {
    this.positionAndSampleMap = new Map();
    this.plateData.forEach(sample => {
      if(sample.assayType === AssayType.SS_NGS_FRAGMENT) {
        this.assayTypeCounts.SSNGS++;
      } else if(sample.assayType === AssayType.HS_NGS_FRAGMENT) {
        this.assayTypeCounts.HSNGS++;
      }
      this.positionAndSampleMap.set(this.getRowColStringFromPos(sample.positionRow - 1, sample.positionCol -1), sample);
      this.originalPositionAndSampleMap.set(this.getRowColStringFromPos(sample.positionRow - 1, sample.positionCol - 1), this.createSample(sample));
    });
  }

  /**
   * Create Barcode list from Plate Data
   */
  createBarcodeListFromPlateData() {
    this.sampleBarcodeList = [];
    this.plateData.forEach(sample => {
      this.sampleBarcodeList.push(sample.barcode);
    });
  }

  /**
   * Create local instance of incoming samples that are not affected by reference changes
   * @param incomingSample 
   */
  createSample(incomingSample: SampleDetailsDTO) {
    let sample = new SampleDetailsDTO();
    sample.assayType = incomingSample.assayType;
    sample.name = incomingSample.name;
    sample.barcode = incomingSample.barcode;
    sample.concentration = incomingSample.concentration;
    sample.positionCol = incomingSample.positionCol;
    sample.positionRow = incomingSample.positionRow;
    sample.projectName = incomingSample.projectName
    sample.newlyPlaced = incomingSample.newlyPlaced;
    return sample;
  }

  /**
   * Get samples at given row col value for the plate view
   * @param row 
   * @param col 
   */
  getSampleAt(row: number, col: number) {
    let positionString = this.getRowColStringFromPos(row, col);
    if(this.positionAndSampleMap.has(positionString)) {
      return this.positionAndSampleMap.get(positionString);
    }
    return {'barcode': '', 'name': null, 'projectName': null, 'assayType':'NONE', 'concentration':'', 'newlyPlaced': false};
  }

  /**
   * Change assayType of the sample on Click of cell
   * @param row 
   * @param col 
   */
  changeAssayType(row: number, col: number) {
    let positionString = this.getRowColStringFromPos(row, col);
    if(this.positionAndSampleMap.has(positionString)) {
      let sample = this.positionAndSampleMap.get(positionString);
      if(sample.assayType === AssayType.HS_NGS_FRAGMENT) {
        sample.assayType = AssayType.SS_NGS_FRAGMENT;
        this.assayTypeCounts.HSNGS--;
        this.assayTypeCounts.SSNGS++;
        this.commonChangeAssayTypeOperations(positionString, sample);
      } else if(sample.assayType === AssayType.SS_NGS_FRAGMENT) {
        sample.assayType = AssayType.HS_NGS_FRAGMENT;
        this.assayTypeCounts.HSNGS++;
        this.assayTypeCounts.SSNGS--;
        this.commonChangeAssayTypeOperations(positionString, sample);
      }
    }
  }

  /**
   * Common operations after assaytype change
   * @param positionString 
   * @param sample 
   */
  commonChangeAssayTypeOperations(positionString: string, sample: SampleDetailsDTO) {
    if(this.originalPositionAndSampleMap.get(positionString).assayType !== sample.assayType) {
      this.assayTypeChangeMap.set(positionString, sample);
    } else {
      this.assayTypeChangeMap.delete(positionString);
    }
    this.assayTypeChangeClass = 'changed';
  }

  /**
   * Get the positional String from row and col number.
   * Ex: (1, 1) returns 'A1'
   * @param row
   * @param col
   */
  getRowColStringFromPos(row: number, col: number) {
    return this.rowHeaders[row] + this.columnHeaders[col];
  }

  /**
   * Save the assayType changes
   */
  saveAssayTypeChanges() {
    this.blocked = true;
    let assayTypeChangeSampleList: SampleDetailsDTO[] = [];
    this.assayTypeChangeMap.forEach((v, k) => {
      assayTypeChangeSampleList.push(v);
    });
    this.ngsLibraryQCService.saveAssayTypeChanges(assayTypeChangeSampleList).subscribe(
      results => {
        this._messagesService.addSuccessMessage({
          severity: 'success', summary: 'AssayType changes saved',
          detail: results.data
        });
        this.assayTypeChangeMap = new Map();
        this.blocked = false;
        this.assayTypeChangeClass = 'normal';
        assayTypeChangeSampleList.forEach(sample => {
          this.originalPositionAndSampleMap.set(this.getRowColStringFromPos(sample.positionRow - 1, sample.positionCol -1), this.createSample(sample));
        });
      },
      error => {
        this.blocked = false;
        let message = this.errorHandler.handleError(error);
        this._messagesService.addErrorMessage(message);
      }
    );
  }

  // On Machine type changes
  onMachineTypeChange(machineType: string) {
    console.log('machineType---> : ' + machineType);
    if (machineType != null) {
      this.selectedMachineType = machineType;
    }
  }

  // generate Hamilton file and FA workfiles
  generateWorkFiles() {
    this.blocked = true;
    //get barcode list out of plate data
    this.createBarcodeListFromPlateData();
    let libQCFAWorkFileRequest = new LibQCFAWorkFileRequest();
    if(!this.selectedMachineType){
      this._messagesService.addErrorMessage({
        severity: 'errror', summary: 'Invalid Data',
        detail: 'Please select FA Machine'
      });
      return;
    }
    if(!this.faPlateBarcode){
      this._messagesService.addErrorMessage({
        severity: 'errror', summary: 'Invalid Data',
        detail: 'Please provide valid FA plate'
      });
      return;
    }
    libQCFAWorkFileRequest.libraryBarcodes = this.sampleBarcodeList;
    libQCFAWorkFileRequest.faPlateBarcode = this.faPlateBarcode;
    libQCFAWorkFileRequest.machineType = this.selectedMachineType;
    
    let currentDate = new Date();
    let dateValue  = this.datePipe.transform(currentDate, 'yy-MM-dd HH:mm');
    let str1 = dateValue.toString().replace(/-/g,'');
    let str2 = str1.toString().replace(/:/g,'');
    let filePrefix = str2.toString().replace(' ','_');

    let ngsWorkfileRespose = new NGSWorkfileResponse();
    //Call the service to generate the work files for Lib QC FA
    this.ngsLibraryQCService.generateLibQCFAWorkfiles(libQCFAWorkFileRequest).subscribe(
      results => {
        if(!results) {
          this.blocked = false;
          return;
        }
        ngsWorkfileRespose = results.data;
        let csvData = ngsWorkfileRespose.csvWorkfile;
        let blobCsv: Blob = new Blob([csvData], { type: 'text/plain' });
        let fileCsv: string = filePrefix + '_Transfer_LibQC.csv';
        saveByFileSaver(blobCsv, fileCsv);

        let txtData = ngsWorkfileRespose.txtDataFile;
        let blobTxt: Blob = new Blob([txtData], { type: 'text/plain' });
        let fileTxt: string = filePrefix + '_FA_Plate_LibQC.txt';
        saveByFileSaver(blobTxt, fileTxt);

        this.faPlateBarcode = '';
        this.blocked = false;
      },
      error => {
        this.blocked = false;
        let errMessage = this.errorHandler.handleError(error);
        this._messagesService.addErrorMessage(errMessage);
      }
    );
    
  }

  /**
   * Show plate status for FA_EQC plates
   */
  showPlateStatuses() {
    this.ngsFragmentAnalysisService.getFAPlateList().subscribe(
      resutls => {
        this.faPlates = resutls.data;
        this.showPlateData = true;
      },
      error => {
        let errMessage = this.errorHandler.handleError(error);
        this._messagesService.addErrorMessage(errMessage);
      }
    );
  }

  onPopUpClose() {
    this.showPlateData = !this.showPlateData;
  }
  
  /**
   * Activate save button
   */
  activateSaveButton() {
    let activate = this.assayTypeChangeMap.size > 0;
    if(activate === false) {
      this.assayTypeChangeClass = 'normal';
    } else {
      this.assayTypeChangeClass = 'changed';
    }
    return activate;
  }
  
}